import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Quote, Info, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../GraderUI";
import { ProviderCitationCards } from "./ProviderCitationCards";
import { CitationDataGrid } from "./CitationDataGrid";
import { CustomCitationQuestions } from "./CustomCitationQuestions";
import {
	listAiVisibilityCustomPrompts,
	getAiVisibilityCitationSummary,
} from "../../../services/aiVisibilityApi";
import { buildCitationGridResults } from "./citationGridUtils";

export default function CitationTrackingSection({
	organicData,
	webSearchData,
	websiteId,
	auditId,
	onReportRefresh,
}) {
	const [showMethodology, setShowMethodology] = useState(false);
	const [mode, setMode] = useState("organic");
	const [customPrompts, setCustomPrompts] = useState([]);
	const [citationSummary, setCitationSummary] = useState(null);
	const [customPromptsLoading, setCustomPromptsLoading] = useState(false);
	const [customPromptsError, setCustomPromptsError] = useState(null);

	const loadCustomPrompts = useCallback(async () => {
		if (!websiteId || !auditId) return;
		setCustomPromptsLoading(true);
		setCustomPromptsError(null);

		const [listResult, summaryResult] = await Promise.all([
			listAiVisibilityCustomPrompts(websiteId, auditId),
			getAiVisibilityCitationSummary(websiteId, auditId),
		]);

		setCustomPromptsLoading(false);

		if (!listResult.ok) {
			setCustomPromptsError(listResult.message || "Failed to load custom prompts");
			setCustomPrompts([]);
		} else {
			setCustomPrompts(listResult.prompts);
		}

		if (summaryResult.ok) {
			setCitationSummary(summaryResult.summary);
		}
	}, [websiteId, auditId]);

	useEffect(() => {
		loadCustomPrompts();
	}, [loadCustomPrompts]);

	const hasWebSearch = Boolean(webSearchData);
	const data = mode === "organic" ? organicData : (webSearchData ?? organicData);

	if (!organicData && !webSearchData) {
		return (
			<div className="report-section">
				<p className="report-section__subtitle">No citation tracking data available</p>
			</div>
		);
	}

	const { queries = [], per_provider: perProvider = {}, aggregate_metrics: aggregateMetrics = {}, query_types: queryTypes, query_quality: queryQuality, query_generation_failed: queryGenerationFailed } = data;
	const providers = Object.keys(perProvider).filter((p) => perProvider[p]);

	const gridResults = buildCitationGridResults({
		queries,
		queryTypes,
		perProvider,
		customPrompts,
		mode,
		hasWebSearch,
	});
	const customRowCount = gridResults.length - queries.length;
	const totalQueryCount = queries.length + customRowCount;

	const organicMetrics = organicData?.aggregate_metrics ?? {};
	const webSearchMetrics = webSearchData?.aggregate_metrics ?? {};

	const authorityScore = organicMetrics.ai_authority_score;
	const discoverabilityScore =
		webSearchMetrics.ai_discoverability_score ??
		webSearchMetrics.discoverability_score ??
		organicMetrics.ai_discoverability_score ??
		organicMetrics.discoverability_score;

	const showScoreGrid = authorityScore != null || discoverabilityScore != null;

	return (
		<motion.div
			className="report-section"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
		>
			<div className="report-section__header">
				<div className="report-section__icon-wrap report-section__icon-wrap--purple">
					<Quote className="report-section__icon" />
				</div>
				<div className="report-section__header-text">
					<h2 className="report-section__title">AI Citation Tracking</h2>
					<p className="report-section__subtitle">
						Dual testing: Organic (training data) + Web Search (real-time discoverability)
					</p>
				</div>
			</div>

			{showScoreGrid && (
				<div className="citation-tracking__score-grid">
					{authorityScore != null && (
						<div
							className={`citation-tracking__score-card citation-tracking__score-card--authority${
								hasWebSearch && mode !== "organic"
									? " citation-tracking__score-card--dimmed"
									: ""
							}`}
						>
							<div className="citation-tracking__score-card-header">
								<div>
									<p
										className="citation-tracking__score-card-label"
										style={{ color: "#1e3a8a" }}
									>
										AI Authority Score
									</p>
									<p
										className="citation-tracking__score-card-hint"
										style={{ color: "#1d4ed8" }}
									>
										Organic citation (no web search)
									</p>
								</div>
								<Info className="citation-tracking__methodology-icon" style={{ color: "#2563eb" }} />
							</div>
							<div className="citation-tracking__score-card-value">
								<span
									className="citation-tracking__score-card-number"
									style={{ color: "#1e3a8a" }}
								>
									{authorityScore.toFixed(0)}
								</span>
								<span
									className="citation-tracking__score-card-max"
									style={{ color: "#1d4ed8" }}
								>
									/100
								</span>
							</div>
							<p
								className="citation-tracking__score-card-hint"
								style={{ color: "#2563eb", marginTop: "0.5rem" }}
							>
								Tests if AI models know your brand from training data
							</p>
						</div>
					)}

					{discoverabilityScore != null && (
						<div
							className={`citation-tracking__score-card citation-tracking__score-card--discoverability${
								hasWebSearch && mode !== "websearch"
									? " citation-tracking__score-card--dimmed"
									: ""
							}`}
						>
							<div className="citation-tracking__score-card-header">
								<div>
									<p
										className="citation-tracking__score-card-label"
										style={{ color: "#064e3b" }}
									>
										AI Discoverability Score
									</p>
									<p
										className="citation-tracking__score-card-hint"
										style={{ color: "#047857" }}
									>
										Web search citation (real-time)
									</p>
								</div>
								<Info className="citation-tracking__methodology-icon" style={{ color: "#059669" }} />
							</div>
							<div className="citation-tracking__score-card-value">
								<span
									className="citation-tracking__score-card-number"
									style={{ color: "#064e3b" }}
								>
									{discoverabilityScore.toFixed(0)}
								</span>
								<span
									className="citation-tracking__score-card-max"
									style={{ color: "#047857" }}
								>
									/100
								</span>
							</div>
							<p
								className="citation-tracking__score-card-hint"
								style={{ color: "#059669", marginTop: "0.5rem" }}
							>
								Tests if AI models find you via web search
							</p>
						</div>
					)}
				</div>
			)}

			{hasWebSearch && (
				<div className="citation-tracking__mode-toggle" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={mode === "organic"}
						className={`citation-tracking__mode-btn ${
							mode === "organic" ? "is-active is-active--organic" : ""
						}`}
						onClick={() => setMode("organic")}
					>
						Organic (AI Authority)
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={mode === "websearch"}
						className={`citation-tracking__mode-btn ${
							mode === "websearch" ? "is-active is-active--websearch" : ""
						}`}
						onClick={() => setMode("websearch")}
					>
						Web Search (AI Discoverability)
					</button>
				</div>
			)}

			{queryQuality === "low" && (
				<Alert variant="warning" style={{ marginBottom: "1rem" }}>
					<div style={{ display: "flex", gap: "0.75rem" }}>
						<AlertTriangle className="citation-tracking__methodology-icon" style={{ flexShrink: 0, marginTop: "0.125rem" }} />
						<div>
							<AlertTitle>Query Relevance Limited</AlertTitle>
							<AlertDescription>
								Query relevance may be limited for this audit. Re-run the audit to generate
								improved queries.
							</AlertDescription>
						</div>
					</div>
				</Alert>
			)}

			{queryGenerationFailed && (
				<Alert variant="destructive" style={{ marginBottom: "1rem" }}>
					<div style={{ display: "flex", gap: "0.75rem" }}>
						<AlertTriangle className="citation-tracking__methodology-icon" style={{ flexShrink: 0, marginTop: "0.125rem" }} />
						<div>
							<AlertTitle>Query Generation Failed</AlertTitle>
							<AlertDescription>
								Unable to generate site-specific queries — crawl data insufficient.
							</AlertDescription>
						</div>
					</div>
				</Alert>
			)}

			<ProviderCitationCards perProvider={perProvider} />

			<div style={{ marginBottom: "1rem" }}>
				<button
					type="button"
					className="citation-tracking__methodology-toggle"
					onClick={() => setShowMethodology(!showMethodology)}
				>
					<Info className="citation-tracking__methodology-icon" />
					How Citation Tracking Works
					{showMethodology ? (
						<ChevronUp className="citation-tracking__methodology-icon" />
					) : (
						<ChevronDown className="citation-tracking__methodology-icon" />
					)}
				</button>

				{showMethodology && (
					<motion.div
						className="citation-tracking__methodology-panel"
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
					>
						<h4 style={{ fontWeight: 600, margin: "0 0 0.5rem" }}>Real User Intent Testing</h4>
						<p style={{ margin: "0 0 0.5rem" }}>
							We generate {queries.length} real user intent queries based on your site content,
							then send each query to {providers.length} AI providers without any system prompts
							to simulate real user interactions.
						</p>
						<p style={{ fontWeight: 600, margin: "0.75rem 0 0.25rem" }}>Citation Detection:</p>
						<ul>
							<li>
								<strong>Domain Mention:</strong> Direct reference to your website URL
							</li>
							<li>
								<strong>Brand Mention:</strong> Reference to your brand name
							</li>
							<li>
								<strong>Citation Language:</strong> Phrases like &quot;according to&quot;,
								&quot;source:&quot;, etc.
							</li>
						</ul>
					</motion.div>
				)}
			</div>

			<h3 className="citation-tracking__results-header">Citation Results by Provider</h3>
			<p className="citation-tracking__results-meta">
				{mode === "websearch" ? "Web search (real-time)" : "Organic (training data)"}:{" "}
				{totalQueryCount * providers.length} total checks ({totalQueryCount} queries ×{" "}
				{providers.length} providers)
			</p>

			<CitationDataGrid results={gridResults} />

			{websiteId && auditId && (
				<CustomCitationQuestions
					websiteId={websiteId}
					auditId={auditId}
					prompts={customPrompts}
					summary={citationSummary}
					loading={customPromptsLoading}
					listError={customPromptsError}
					onPromptsChanged={loadCustomPrompts}
					onReportRefresh={onReportRefresh}
				/>
			)}
		</motion.div>
	);
}
